import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  alpha,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@paneon/theme';
import type { Pet } from '../services/api';

interface AddFeedingDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: FeedingFormData) => Promise<void>;
  pets: Pet[];
  defaultPetId?: string;
}

export interface FeedingFormData {
  pet_id: string;
  feeding_type: string;
  food_type: string;
  food_name?: string;
  brand?: string;
  amount_grams?: number;
  notes?: string;
}

const feedingTypes = [
  { value: 'breakfast', label: 'Frühstück', emoji: '🌅' },
  { value: 'lunch', label: 'Mittag', emoji: '☀️' },
  { value: 'dinner', label: 'Abend', emoji: '🌙' },
  { value: 'snack', label: 'Snack', emoji: '🍪' },
];

const foodTypes = [
  { value: 'dry', label: 'Trockenfutter' },
  { value: 'wet', label: 'Nassfutter' },
  { value: 'raw', label: 'Rohfutter (BARF)' },
  { value: 'homemade', label: 'Selbstgekocht' },
  { value: 'treat', label: 'Leckerli' },
];

export function AddFeedingDialog({
  open,
  onClose,
  onSave,
  pets,
  defaultPetId,
}: AddFeedingDialogProps) {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FeedingFormData>({
    pet_id: defaultPetId || '',
    feeding_type: 'dinner',
    food_type: 'dry',
    food_name: '',
    brand: '',
    amount_grams: undefined,
    notes: '',
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const currentHour = new Date().getHours();
      let defaultFeedingType = 'dinner';
      if (currentHour < 10) defaultFeedingType = 'breakfast';
      else if (currentHour < 14) defaultFeedingType = 'lunch';
      else if (currentHour < 18) defaultFeedingType = 'snack';

      setForm({
        pet_id: defaultPetId || pets[0]?.id || '',
        feeding_type: defaultFeedingType,
        food_type: 'dry',
        food_name: '',
        brand: '',
        amount_grams: undefined,
        notes: '',
      });
    }
  }, [open, defaultPetId, pets]);

  const handleSubmit = async () => {
    if (!form.pet_id || !form.feeding_type || !form.food_type) return;

    setLoading(true);
    try {
      await onSave({
        ...form,
        food_name: form.food_name?.trim() || undefined,
        brand: form.brand?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const dialogStyles = {
    '& .MuiDialog-paper': {
      borderRadius: 3,
      backgroundColor: isDarkMode ? alpha('#1a1a1a', 0.95) : alpha('#fff', 0.95),
      backdropFilter: 'blur(20px)',
      maxWidth: 400,
      width: '100%',
    },
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDarkMode ? alpha('#fff', 0.05) : alpha('#000', 0.03),
    },
  };

  return (
    <Dialog open={open} onClose={onClose} sx={dialogStyles}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        Fütterung eintragen
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Pet Selection */}
          <FormControl fullWidth sx={inputStyles}>
            <InputLabel>Haustier</InputLabel>
            <Select
              value={form.pet_id}
              label="Haustier"
              onChange={e => setForm(f => ({ ...f, pet_id: e.target.value }))}
            >
              {pets.map(pet => (
                <MenuItem key={pet.id} value={pet.id}>
                  {pet.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Feeding Type Toggle */}
          <Box>
            <ToggleButtonGroup
              value={form.feeding_type}
              exclusive
              onChange={(_, value) => value && setForm(f => ({ ...f, feeding_type: value }))}
              fullWidth
              size="small"
            >
              {feedingTypes.map(type => (
                <ToggleButton
                  key={type.value}
                  value={type.value}
                  sx={{
                    textTransform: 'none',
                    py: 1,
                    '&.Mui-selected': {
                      backgroundColor: alpha('#4CAF50', 0.2),
                      color: '#4CAF50',
                    },
                  }}
                >
                  {type.emoji} {type.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Food Type */}
          <FormControl fullWidth sx={inputStyles}>
            <InputLabel>Futterart</InputLabel>
            <Select
              value={form.food_type}
              label="Futterart"
              onChange={e => setForm(f => ({ ...f, food_type: e.target.value }))}
            >
              {foodTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Food Name */}
          <TextField
            label="Futtername (optional)"
            value={form.food_name}
            onChange={e => setForm(f => ({ ...f, food_name: e.target.value }))}
            fullWidth
            sx={inputStyles}
            placeholder="z.B. Royal Canin Adult"
          />

          {/* Amount */}
          <TextField
            label="Menge (g)"
            type="number"
            value={form.amount_grams || ''}
            onChange={e =>
              setForm(f => ({
                ...f,
                amount_grams: e.target.value ? parseInt(e.target.value, 10) : undefined,
              }))
            }
            fullWidth
            slotProps={{
              htmlInput: { step: 10, min: 0 },
            }}
            sx={inputStyles}
          />

          {/* Notes */}
          <TextField
            label="Notizen (optional)"
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            fullWidth
            multiline
            rows={2}
            sx={inputStyles}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !form.pet_id}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {loading ? 'Speichern...' : 'Eintragen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
